import {
  createHashHistory,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  RouterProvider,
} from "@tanstack/react-router";
import { SeasonListUI } from "./books/SeasonListUI";
import { RootLayout } from "./RootLayout";

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: SeasonListUI,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    query?: string;
    categoryId?: string;
    genreId?: string;
    sortBy?: string;
    page?: number;
    seriesId?: number;
  } => ({
    query: (search.query as string) || undefined,
    categoryId: (search.categoryId as string) || undefined,
    genreId: (search.genreId as string) || undefined,
    sortBy: (search.sortBy as string) || undefined,
    page: Number(search.page) || undefined,
    seriesId: Number(search.seriesId) || undefined,
  }),
});

const playRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/play/$chapterId",
  component: lazyRouteComponent(
    () => import("./audiobook/AudioBook"),
    "AudioBook",
  ),
});

const validateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/validate/$chapterId",
  component: lazyRouteComponent(
    () => import("./validation/MageBookValidation"),
    "MageBookValidation",
  ),
});

const tasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$chapterId",
  component: lazyRouteComponent(() => import("./tasks/BookTasks"), "BookTasks"),
});

const taskDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks/$chapterId/$taskId",
  component: lazyRouteComponent(
    () => import("./tasks/BookTaskDetail"),
    "BookTaskDetail",
  ),
});

const postProdRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/postprod/$chapterId",
  component: lazyRouteComponent(
    () => import("./postproduction/PostProduction"),
    "PostProduction",
  ),
});

const publishRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/publish/$chapterId",
  // `task` + `action` keep the just-started action's notice alive across
  // refreshes and round-trips to the task log.
  validateSearch: (
    search: Record<string, unknown>,
  ): { task?: string; action?: string } => ({
    task: typeof search.task === "string" ? search.task : undefined,
    action: typeof search.action === "string" ? search.action : undefined,
  }),
  component: lazyRouteComponent(
    () => import("./publishing/PublishPage"),
    "PublishPage",
  ),
});

// Routes that belong to no episode. They render inside the same RootLayout and are
// reached from the global sidebar rather than from an episode's toolbar.

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/calendar",
  component: lazyRouteComponent(
    () => import("./calendar/ReleaseCalendarPage"),
    "ReleaseCalendarPage",
  ),
});

// Sibling of "/tasks/$chapterId", not a parent of it: this one is the user's whole
// task history, that one is a single episode's.
const allTasksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/tasks",
  component: lazyRouteComponent(
    () => import("./tasks/AllTasksPage"),
    "AllTasksPage",
  ),
});

// Exported so tests can mount routes with a memory history instead of the
// hash history below, which reads window.location.
export const routeTree = rootRoute.addChildren([
  indexRoute,
  playRoute,
  validateRoute,
  tasksRoute,
  taskDetailRoute,
  postProdRoute,
  publishRoute,
  calendarRoute,
  allTasksRoute,
]);

const hashHistory = createHashHistory();

const router = createRouter({
  routeTree,
  history: hashHistory,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return <RouterProvider router={router} />;
}
